import { Button, ButtonSizes, ButtonVariants, Icon, IconNames } from '@triumph/design-system';
import { useState } from 'react';
import { SideDrawer } from '@/components/shared/components/SideDrawer';
import { AppliedFilterChips } from './AppliedFilterChips';
import { FilterCategoryPicker, FilterCategorySearch } from './FilterCategoryPicker';
import {
    ConfiguredFilterLabel,
    ConfiguredFilterLabelRow,
    ConfiguredFilterList,
    ConfiguredFilterRemoveButton,
    DrawerDivider,
} from './FilterDrawer.styles';
import { FilterFieldRenderer } from './FilterFieldRenderer';
import { FilterDefinition } from './types';
import { UseFiltersResult } from './useFilters';

export interface FilterDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    registry: FilterDefinition[];
    useFiltersResult: UseFiltersResult;
    rendererPropsByFilterId?: Record<string, Record<string, unknown>>;
}

export function FilterDrawer({
    isOpen,
    onClose,
    registry,
    useFiltersResult,
    rendererPropsByFilterId,
}: FilterDrawerProps) {
    const {
        picked,
        appliedChips,
        removeApplied,
        togglePicked,
        getPending,
        setPending,
        apply,
        reset,
    } = useFiltersResult;

    const [searchQuery, setSearchQuery] = useState('');

    const pickedFilters = registry.filter((f) => picked.has(f.id));

    return (
        <SideDrawer
            isOpen={isOpen}
            onClose={onClose}
            title="Filters"
            position="left"
            modal
            widthPx={320}
            closeLabel="Close filters"
            bodyTestId="filter-drawer"
            scrollHintTestId="filter-drawer-scroll-hint"
            scrollHintLabel="Scroll for more filters"
            footer={
                <>
                    <Button
                        variant={ButtonVariants.secondary}
                        size={ButtonSizes.small}
                        onClick={reset}
                        data-testid="filter-drawer-reset"
                    >
                        Reset
                    </Button>
                    <Button
                        variant={ButtonVariants.primary}
                        size={ButtonSizes.small}
                        onClick={() => {
                            apply();
                            onClose();
                        }}
                        data-testid="filter-drawer-apply"
                    >
                        Apply Filters
                    </Button>
                </>
            }
        >
            <FilterCategorySearch query={searchQuery} onQueryChange={setSearchQuery} />
            {appliedChips.length > 0 && (
                <>
                    <DrawerDivider />
                    <AppliedFilterChips
                        layout="stacked"
                        filters={appliedChips}
                        onRemove={removeApplied}
                    />
                </>
            )}
            <DrawerDivider />
            <FilterCategoryPicker
                registry={registry}
                picked={picked}
                onToggle={togglePicked}
                query={searchQuery}
            />
            {pickedFilters.length > 0 && (
                <>
                    <DrawerDivider />
                    <ConfiguredFilterList>
                        {pickedFilters.map((f) => (
                            <div key={f.id}>
                                <ConfiguredFilterLabelRow>
                                    <ConfiguredFilterLabel htmlFor={`filter-field-${f.id}`}>
                                        {f.label}
                                    </ConfiguredFilterLabel>
                                    <ConfiguredFilterRemoveButton
                                        type="button"
                                        data-testid={`configured-filter-remove-${f.id}`}
                                        aria-label={`Remove ${f.label} filter`}
                                        onClick={() => togglePicked(f.id)}
                                    >
                                        <Icon
                                            name={IconNames.Danger_CloseX}
                                            size={14}
                                            id={`configured-filter-remove-icon-${f.id}`}
                                        />
                                    </ConfiguredFilterRemoveButton>
                                </ConfiguredFilterLabelRow>
                                <FilterFieldRenderer
                                    filter={f}
                                    value={getPending(f.id)}
                                    onChange={(v) => setPending(f.id, v)}
                                    rendererPropsOverride={rendererPropsByFilterId?.[f.id]}
                                />
                            </div>
                        ))}
                    </ConfiguredFilterList>
                </>
            )}
        </SideDrawer>
    );
}
